import { formatDate, registerLocaleData } from '@angular/common';
import localeArTn from '@angular/common/locales/ar-TN';
import localeFr from '@angular/common/locales/fr';
import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

// L'anglais est livré avec Angular ; l'arabe et le français s'enregistrent.
registerLocaleData(localeArTn);
registerLocaleData(localeFr);

/**
 * Langue de l'interface → données de locale.
 *
 * <p>L'arabe de Tunisie plutôt que l'arabe générique : ses mois sont ceux
 * qu'on lit ici (جانفي، فيفري…) et ses chiffres restent latins, comme dans le
 * reste de l'application.</p>
 */
const LOCALES: Record<string, string> = { ar: 'ar-TN', fr: 'fr', en: 'en-US' };

/**
 * Le `date` d'Angular, dans la langue choisie par l'utilisateur.
 *
 * <p>Sans locale enregistrée, `date` formate toujours en anglais : le panneau
 * d'une séance affichait « Thursday 01 October 2026 » au milieu d'une
 * interface en arabe. Mêmes formats que `date`, seule la langue suit.</p>
 *
 * <p>Impur, comme `duration` : il doit suivre un changement de langue sans
 * que la date change.</p>
 *
 * Usage : {{ value | localizedDate:'EEEE dd MMMM yyyy' }}
 */
@Pipe({
  name: 'localizedDate',
  standalone: true,
  pure: false,
})
export class LocalizedDatePipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  // Impur, il est appelé à chaque détection : on ne reformate que si la date,
  // le format ou la langue ont changé.
  private last?: { value: unknown; format: string; timezone?: string; locale: string; text: string };

  transform(
    value: string | number | Date | null | undefined,
    format = 'mediumDate',
    timezone?: string,
  ): string {
    if (value == null || value === '') return '';
    const locale = LOCALES[this.translate.currentLang] ?? LOCALES['ar'];
    const key = value instanceof Date ? value.getTime() : value;
    const last = this.last;
    if (last && last.value === key && last.format === format && last.timezone === timezone && last.locale === locale) {
      return last.text;
    }
    const text = formatDate(value, format, locale, timezone);
    this.last = { value: key, format, timezone, locale, text };
    return text;
  }
}
